import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { Button } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { OverlayState } from '@/hooks/useOverlay'
import { useUpdateAuthUser } from '@/service-library/hooks/auth-user'
import { useInviteMember } from '@/services/hooks/stytch'
import { DEFAULT_USER_ROLE } from '@/utils/auth-user'
import { showErrorSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { PixyDocsForm } from '@/components/forms'
import UserForm from './UserCoreInfo'

type FormValues = {
  name: string
  email_address: string
  role_id: string
  employee_ext_id: string
}

type AddUserDialogProps = {
  overlay: OverlayState
}

const DEFAULT_VALUES = {
  name: '',
  email_address: '',
  employee_ext_id: '',
  role_id: DEFAULT_USER_ROLE.id,
}

export default function AddUserDialog({ overlay }: AddUserDialogProps) {
  const navigate = useNavigate()
  const stytch = useStytchB2BClient()
  const { inviteMember } = useInviteMember()
  const { updateAuthUserAsync } = useUpdateAuthUser()

  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  })

  const {
    formState: { isDirty, isValid },
    reset,
  } = methods

  async function handleCreateUser({
    name,
    email_address,
    role_id,
    employee_ext_id,
  }: FormValues) {
    try {
      const { member } = await stytch.organization.members.create({
        name,
        email_address,
        create_member_as_pending: true,
      })

      if (role_id !== DEFAULT_USER_ROLE.id || employee_ext_id !== '') {
        try {
          await updateAuthUserAsync({
            id: member.member_id,
            roles_ids: role_id !== DEFAULT_USER_ROLE.id ? [role_id] : undefined,
            employee_ext_id:
              employee_ext_id !== '' ? employee_ext_id : undefined,
          })
        } catch {
          showErrorSnackbar(
            'Unable to set some fields to the new user. Please try again later.',
          )
        }
      }
      inviteMember({ email_address: member.email_address })
      navigate(`/settings/users/${member.member_id}`)
    } catch {
      showErrorSnackbar('Unable to create user. Please try again later.')
    } finally {
      overlay.close()
    }
  }

  useEffect(() => {
    if (!overlay.isOpen) {
      reset(DEFAULT_VALUES)
    }
  }, [overlay.isOpen, reset])

  return (
    <Dialog {...overlay} maxWidth="sm" title="Add User">
      <DialogContent disablePadding>
        <PixyDocsForm
          id="user-form"
          methods={methods}
          onSubmit={handleCreateUser}
        >
          <UserForm overlay={overlay} />
        </PixyDocsForm>
      </DialogContent>

      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Cancel
        </Button>
        <Button
          form="user-form"
          disabled={!isDirty || !isValid}
          variant="contained"
          type="submit"
        >
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
